import type { DialogStore } from '@ariakit/react';
import { Dialog } from '@ariakit/react';
import RiDeleteBinLine from '@ben/icons/delete-bin-line';
import type { ITheme } from '@ben/orion/utils/theme';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import RIcon from '@ben/orion/remix-icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { FC } from 'react';
import { memo, useCallback } from 'react';
import type { DefaultTheme } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
interface IDeleteConfirmationModalProps {
  dialog: DialogStore;
  filename: string;
  handleDeleteTable: () => void;
}

const DeleteConfirmationModal: FC<IDeleteConfirmationModalProps> = ({
  dialog,
  filename,
  handleDeleteTable,
}) => {
  const theme = useTheme() as ITheme & DefaultTheme;

  const handleDelete = useCallback(() => {
    handleDeleteTable();
    dialog.hide();
  }, [dialog, handleDeleteTable]);

  return (
    <DialogContainer store={dialog} aria-modal="true">
      <DialogContent>
        <TrashContainer>
          <RIcon
            icon={RiDeleteBinLine}
            size="lg"
            color={theme.palette.error.main}
          />
        </TrashContainer>
        <ContentContainer>
          <Typography variant={TypographyVariant.h2}>
            Delete &apos;{filename}&apos;
          </Typography>
          <Typography variant={TypographyVariant.bodyLg1}>
            Are you sure you want to delete this file?
          </Typography>
          <Typography variant={TypographyVariant.bodyLg1}>
            This action cannot be undone.
          </Typography>
        </ContentContainer>
        <ActionsContainer>
          <Button
            label="Cancel"
            variant={ButtonVariant.secondary}
            onClick={() => dialog.hide()}
            width="208px"
          />
          <Button
            label="Delete"
            variant={ButtonVariant.error}
            onClick={handleDelete}
            width="208px"
          />
        </ActionsContainer>
      </DialogContent>
    </DialogContainer>
  );
};

export default memo(DeleteConfirmationModal);

const DialogContainer = styled(Dialog)`
  ${({ theme }) => css`
    position: fixed;
    inset: 0.75rem;
    z-index: 50;
    margin: auto;
    background-color: ${theme.palette.black(0.7)};
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: ${theme.spacing(-1.5)};
    width: 100vw;
    height: 100vh;
  `}
`;

const DialogContent = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.palette.neutral['00']};
    border-radius: ${theme.borderRadius.sm};
    padding: ${theme.spacing(5)};
    width: 520px;
    height: fit-content;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    align-items: center;
    justify-content: center;
  `}
`;

const TrashContainer = styled.div`
  ${({ theme }) => css`
    width: 80px;
    height: 80px;
    border-radius: ${theme.borderRadius.full};
    background-color: ${theme.palette.error.bg};
    display: flex;
    justify-content: center;
    align-items: center;
  `}
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    margin-top: ${theme.spacing(3)};
  `}
`;

const ActionsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: ${theme.spacing(5)};
    width: 100%;
  `}
`;
